import * as React from "react"
import {connect} from "react-redux"
import {
    Modal,
    Button,
    Message,
    Icon,
    Divider,
    Input,
    InputOnChangeData,
} from "semantic-ui-react"
import ExcelJsCus, {ValueType} from "exceljscus"
import {saveAs} from "file-saver"
import {
    getActiveLang,
    getTranslationsByScopes,
    DropitState,
    getUserRole,
    getUserSelectedSupplier,
} from "../../core/reducers"
import {
    IRawSupplierReference,
    IReferenceIds,
    ISupplierInfo,
    ITranslations,
    completeRawDeclined,
} from "../../models"
import {ProductFormState} from "../product-form/_reducers"
import {getActions, ProductFormActions} from "../product-form/_actions"
import {$sendMultiModifPriceInformations} from "../../core/requests"
import {fetchProducts} from "../../core/actions"
import {Languages, Roles, isDroopyGuid} from "../../utils"

import Loading from "./com_loading"
import {Multilang} from "../../models/product/multilang-string"
import ReactTable from "react-table"
import {getTranslations} from "../../utils/table/methods"
import moment from "moment"
import {IProductRefVente} from "../../models/product/productRefVente"
import {getRefVenteIndex} from "./com_product-list-columns"

// ------------------------ VIEW ------------------------
interface IMultiModifPricesModalProps {
    state: ProductFormState
    actions: ProductFormActions
    //   formId: "multiModif"
    translations: ITranslations["fieldErrors"] &
        ITranslations["multiModif"] &
        ITranslations["productForm"] &
        ITranslations["productList"]
    selectedProducts: IReferenceIds[]
    //   customs: ICustoms[]
    //   territoi: ITerritoi[]
    //   brands: string[]
    //   vat: IVat[]
    //   techPropNodes: ITreeNode[]
    //   techRawData: ITreeNode[]
    //   comPropNodes: ITreeNode[]
    //   comRawData: ITreeNode[]
    activeLang: Languages
    supplier: ISupplierInfo
    role: Roles
    fullProductList: IRawSupplierReference[]
    refVentes: IProductRefVente[]
    refreshProductList: () => Promise<boolean>
}

interface IDroopyIdAndPrices {
    droopyId: string
    supplierRef: string
    refVente?: string
    label: string
    oldPrice: number
    isTopRef: boolean
    topRef: string | undefined
    newPrice: number | undefined
}

// ExcelJS is only needed for the price import/export: it is loaded on demand (separate chunk) instead of a
// non-minified 2.6 MB script blocking every page load (FE-PERF-R09).
const loadExcelJs = (): Promise<typeof ExcelJsCus> =>
    import("exceljs").then((m: any) => (m.default || m) as typeof ExcelJsCus)

const MultiModifPricesModal = ({
                                   state,
                                   actions,
                                   activeLang,
                                   translations,
                                   selectedProducts,
                                   role,
                                   supplier,
                                   fullProductList,
                                   refreshProductList,
                                   refVentes = [],
                               }: IMultiModifPricesModalProps) => {
    const [isLocalLoading, setIsLocalLoading] = React.useState(false)
    const [droopyIdsAndPrices, setDroopyIdsAndPrices] = React.useState<
        IDroopyIdAndPrices[]
    >([])
    const [open, setOpen] = React.useState(false)
    const handleOpen = () => setOpen(true)
    const handleClose = () => {
        setOpen(false)
        setIsLocalLoading(false)
    }

    // Only computed while the modal is open (it ran on every selection change of the list).
    React.useEffect(() => {
        if (!open) {
            return
        }
        const selectedTopRefs = new Set(
            selectedProducts
                .filter((product) => product.isTopRef)
                .map((product) => product.supplierRef)
        )
        const selectedIds = new Set(selectedProducts.map((d) => d.droopyId))
        const refVenteIndex = getRefVenteIndex(refVentes)

        const idsAndPrices = fullProductList
            .filter(
                (product) =>
                    selectedIds.has(product.droopyId) ||
                    selectedTopRefs.has(product.topRef || "")
            )
            .reduce((acc: IDroopyIdAndPrices[], product) => {
                const declinedProducts =
                    product.declined
                        ?.filter((x) => !x.deleted)
                        .map((declined) => {
                            const completed = completeRawDeclined(product, declined)
                            return {
                                droopyId: completed.droopyId,
                                supplierRef: completed.supplierRef,
                                label: Multilang.String.GetValue(
                                    completed.label,
                                    activeLang,
                                    true
                                ),
                                isTopRef: false,
                                oldPrice: completed.price,
                                topRef: product.supplierRef,
                                newPrice: undefined,
                            }
                        }) || []

                acc.push(
                    {
                        droopyId: product.droopyId,
                        supplierRef: product.supplierRef,
                        refVente: refVenteIndex.get(product.supplierRef)?.refVente,
                        label: Multilang.String.GetValue(product.label, activeLang, true),
                        oldPrice: product.price,
                        isTopRef: product.isTopRef,
                        topRef: undefined,
                        newPrice: undefined,
                    },
                    ...declinedProducts
                )
                return acc
            }, [])

        setDroopyIdsAndPrices(idsAndPrices)
    }, [activeLang, fullProductList, open, selectedProducts])

    const inputExcel: any = React.createRef()

    const handleFocusInputExcel = (e: any) => {
        e.preventDefault()
        if (inputExcel && inputExcel.current && inputExcel.current) {
            inputExcel.current.click()
        }
    }

    const disabled = selectedProducts.length < 2
    const loading = state.loading > 0 || isLocalLoading

    const handleSubmit = () => {
        const changed = droopyIdsAndPrices.filter(
            (x) =>
                x.newPrice !== x.oldPrice && x.newPrice !== undefined && x.newPrice >= 0
        )
        const toSend = changed.map((o) => ({
            droopyId: o.droopyId,
            newPrice: o.newPrice || o.oldPrice,
            topRef: o.topRef,
        }))

        if (!window.confirm(`${changed.length} ${translations.pricesConfirm}`)) {
            return
        }

        setIsLocalLoading(true)
        $sendMultiModifPriceInformations(
            toSend,
            role,
            supplier.supplierName,
            translations
        )
            .then(() => {
                handleClose()
                refreshProductList()
            })
            // The error is displayed by $myFetch; the modal stays open so the user can retry.
            .catch(() => undefined)
            .finally(() => setIsLocalLoading(false))
    }

    const extractExcelTemplate = () => {
        if (!open) {
            return
        }
        const excelFileName = `dropit${
            droopyIdsAndPrices.length
        }PricesUpdate_${moment().format("YYYY_MM_DD_hh_mm_ss")}`
        setIsLocalLoading(true)
        loadExcelJs()
            .then((ExcelJS) => writeExcelTemplate(ExcelJS, excelFileName))
            .catch((err) => {
                console.error(err)
                setIsLocalLoading(false)
            })
    }

    const writeExcelTemplate = (ExcelJS: typeof ExcelJsCus, excelFileName: string) => {
        const wb = new ExcelJS.Workbook()
        const ws = wb.addWorksheet()
        ws.addRow([
            "Ref. Bruneau",
            translations.pricesExcelColOne,
            translations.pricesExcelColTwo,
            translations.pricesExcelColThree,
            translations.pricesExcelColFour,
            translations.pricesExcelColFive,
        ])
        droopyIdsAndPrices.forEach((r, i: any) => {
            const firstCol = r.isTopRef
                ? "↓ TOPREF ↓"
                : isDroopyGuid(r.supplierRef)
                    ? "Ref. à renseigner"
                    : r.supplierRef
            const row = ws.addRow([
                r.refVente,
                firstCol,
                r.label,
                r.oldPrice,
                r.newPrice || r.oldPrice,
                {formula: `=E${i + 2}/D${i + 2}-1`},
                r.supplierRef,
            ])
        })
        setIsLocalLoading(true)
        ws.getColumn(1).width = 15
        ws.getColumn(1).style = {
            alignment: {horizontal: "center", vertical: "middle"},
        }
        ws.getColumn(2).width = 15
        ws.getColumn(2).style = {
            alignment: {horizontal: "center", vertical: "middle"},
        }
        ws.getColumn(3).width = 80
        ws.getColumn(3).style = {
            alignment: {horizontal: "center", vertical: "middle"},
        }
        ws.getColumn(4).width = 21
        ws.getColumn(4).style = {
            alignment: {horizontal: "center", vertical: "middle"},
        }
        ws.getColumn(5).protection = {locked: false}
        ws.getColumn(5).width = 13
        ws.getColumn(5).style = {
            alignment: {horizontal: "center", vertical: "middle"},
        }
        ws.getColumn(6).width = 15
        ws.getColumn(6).numFmt = "#.##%"
        ws.getColumn(6).style = {
            alignment: {horizontal: "center", vertical: "middle"},
        }
        ws.getColumn(7).width = 0
        ws.getColumn(7).protection = {locked: true}
        ws.getColumn(8).width = 0
        ws.getColumn(8).protection = {locked: true}
        ws.getColumn(9).width = 15
        ws.getColumn(9).protection = {locked: false}
        ws.getColumn(10).width = 15
        ws.getColumn(10).protection = {locked: false}
        ws.getColumn(11).width = 15
        ws.getColumn(11).protection = {locked: false}
        ws.getColumn(12).width = 15
        ws.getColumn(12).protection = {locked: false}
        ws.getColumn(13).width = 15
        ws.getColumn(13).protection = {locked: false}
        ws.getColumn(14).width = 15
        ws.getColumn(14).protection = {locked: false}
        ws.getColumn(15).width = 15
        ws.getColumn(15).protection = {locked: false}
        ws.getColumn(16).width = 15
        ws.getColumn(16).protection = {locked: false}
        ws.getColumn(17).width = 15
        ws.getColumn(17).protection = {locked: false}
        ws.getColumn(18).width = 15
        ws.getColumn(18).protection = {locked: false}
        ws.getColumn(19).width = 15
        ws.getColumn(19).protection = {locked: false}
        ws.getColumn(20).width = 15
        ws.getColumn(20).protection = {locked: false}
        ws.getColumn(21).width = 15
        ws.getColumn(21).protection = {locked: false}
        ws.getColumn(22).width = 15
        ws.getColumn(22).protection = {locked: false}
        ws.getColumn(23).width = 15
        ws.getColumn(23).protection = {locked: false}
        ws.getColumn(24).width = 15
        ws.getColumn(24).protection = {locked: false}
        ws.getColumn(25).width = 15
        ws.getColumn(25).protection = {locked: false}
        ws.getColumn(26).width = 15
        ws.getColumn(26).protection = {locked: false}
        ws.getColumn(27).width = 15
        ws.getColumn(27).protection = {locked: false}
        ws.getColumn(28).width = 15
        ws.getColumn(28).protection = {locked: false}
        ws.getColumn(29).width = 15
        ws.getColumn(29).protection = {locked: false}
        ws.getColumn(30).width = 15
        ws.getColumn(30).protection = {locked: false}

        ws.protect("dropitpass", {
            formatCells: true,
            formatColumns: true,
            formatRows: true,
            insertRows: false,
            insertColumns: false,
            insertHyperlinks: false,
            deleteRows: false,
            deleteColumns: false,
            sort: false,
            autoFilter: false,
            selectUnlockedCells: true,
            selectLockedCells: true,
        }).then(() =>
            wb.xlsx
                .writeBuffer()
                .then((buf: any) => saveAs(new Blob([buf]), excelFileName + ".xlsx"))
                .catch(console.log)
                .finally(() => setIsLocalLoading(false))
        ).catch(console.log)
    }

    const returnNull = () => null
    // Functional update: the callback (and therefore the columns) stays the same while typing (FE-PERF-R12).
    const handleChangeNewPrices = React.useCallback(
        (droopyid: string, supplierRef: string, newPrice: number) => {
            setDroopyIdsAndPrices((current) =>
                current.map((x) =>
                    (!!x.topRef && x.topRef === supplierRef) || x.droopyId === droopyid
                        ? {...x, newPrice}
                        : x
                )
            )
        },
        []
    )
    const columns = React.useMemo(
        () => getColumns({translations, onChangeNewPrice: handleChangeNewPrices}),
        [translations, handleChangeNewPrices]
    )

    const uploadedFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        let file = e.target?.files?.[0]
        if (!file || e.target?.files?.length === 0) {
            return
        }
        setIsLocalLoading(true)
        Promise.all([loadExcelJs(), file.arrayBuffer()]).then(([ExcelJS, v]) => {
            const workbook = new ExcelJS.Workbook()
            return workbook.xlsx
                .load(v)
                .then((w) => {
                    const sheet = w.getWorksheet(1)
                    let supplierRefAndPrice: {
                        supplierRef: string | undefined
                        newPrice: number | undefined
                    }[] = []
                    sheet.eachRow({includeEmpty: false}, (row, rowNumber) => {
                        if (rowNumber === 1) {
                            return
                        }
                        supplierRefAndPrice = [
                            ...supplierRefAndPrice,
                            {
                                supplierRef: (row.findCell(7)?.result ||
                                    row.findCell(7)?.value) as string,
                                newPrice: (row.findCell(5)?.result ||
                                    row.findCell(5)?.value) as number,
                            },
                        ]
                    })
                    const toSave = droopyIdsAndPrices.map((x) => {
                        const found = supplierRefAndPrice.find(
                            (dx) => dx.supplierRef === x.supplierRef
                        )
                        if (found) {
                            return {
                                ...x,
                                newPrice:
                                    Math.round(
                                        (found.newPrice || x.newPrice || x.oldPrice) * 100
                                    ) / 100,
                            }
                        }
                        return x
                    })
                    setDroopyIdsAndPrices([...toSave])
                    // get worksheet, read rows, etc
                })
                .finally(() => setIsLocalLoading(false))
        }).catch((err) => {
            console.error(err)
            setIsLocalLoading(false)
        })
    }

    return (
        <Modal
            trigger={
                <Button
                    className="element button colored-button"
                    disabled={disabled}
                    onClick={handleOpen}
                >
                    {translations.pricesButton}
                </Button>
            }
            onClose={handleClose}
            open={open}
            size={"large"}
            dimmer="inverted"
            className="multi-modal"
        >
            {loading && <Loading/>}
            <Modal.Header>{translations.header}</Modal.Header>
            <Modal.Content scrolling className="prices">
                <Message
                    info
                    icon="warning sign"
                    header={translations.pricesMessageHeader}
                    content={
                        <div>
                            <Divider/>
                            {translations.pricesMessageLineOne}
                            <div className="multi-modif-inline-example">
                                <div className="prices">
                                    <div className="example-text">
                                        {translations.pricesExampleTextTwo}
                                    </div>
                                    <div className="example-button">
                                        <Button
                                            color="green"
                                            basic
                                            type="button"
                                            onClick={extractExcelTemplate}
                                        >
                                            <Icon name={"download"}/>
                                            {translations.pricesDownloadButton}
                                        </Button>
                                        <Icon name="arrow right"/>
                                        <div style={{display: "none"}}>
                                            <input
                                                ref={inputExcel}
                                                type="file"
                                                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                                name="excel"
                                                onChange={uploadedFile}
                                            />
                                        </div>
                                        <Button
                                            color="green"
                                            type="button"
                                            onClick={handleFocusInputExcel}
                                        >
                                            <Icon name={"upload"}/>
                                            {translations.pricesUploadButton}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                            {translations.pricesMessageLineTwo}
                        </div>
                    }
                />
                <ReactTable
                    style={{maxHeight: 400}}
                    className="product-list-table"
                    NoDataComponent={returnNull}
                    loading={loading}
                    data={droopyIdsAndPrices}
                    columns={columns}
                    filterable={false}
                    sortable={false}
                    pageSize={droopyIdsAndPrices.length}
                    showPagination={false}
                    {...getTranslations(translations)}
                />
            </Modal.Content>
            <Modal.Actions>
                <Button
                    inverted
                    color="red"
                    icon="remove"
                    labelPosition="right"
                    content={translations.close}
                    onClick={handleClose}
                />
                <Button
                    inverted
                    color="green"
                    icon="checkmark"
                    labelPosition="right"
                    content={translations.validate}
                    onClick={handleSubmit}
                />
            </Modal.Actions>
        </Modal>
    )
}

export const getColumns = ({
                               translations,
                               onChangeNewPrice,
                           }: {
    onChangeNewPrice: (
        droopyId: string,
        supplierRef: string,
        newPrice: number
    ) => void
    translations: ITranslations["fieldErrors"] &
        ITranslations["multiModif"] &
        ITranslations["productForm"] &
        ITranslations["productList"]
}) => [
    {
        Header: "Ref. Bruneau",
        accessor: "refVente",
        width: 120,
    },
    {
        Header: translations.pricesExcelColOne,
        id: "supplierRef",
        width: 130,
        accessor: (a: IDroopyIdAndPrices) => {
            const isDroopyguid = isDroopyGuid(a.supplierRef)
            const isVariant = !!a.topRef
            return isDroopyguid && !isVariant ? (
                <span style={{textAlign: "left", width: "100%"}}>↓ TOPREF ↓</span>
            ) : (
                <span
                    style={
                        isVariant
                            ? {fontWeight: "bold", textAlign: "left", width: "100%"}
                            : {}
                        // : { textAlign: "left", width: "100%" }
                    }
                >
          {isDroopyguid ? "Ref. à renseigner" : a.supplierRef}
        </span>
            )
        },
    },
    {
        Header: translations.pricesExcelColTwo,
        id: "label",
        accessor: (a: IDroopyIdAndPrices) => (
            <span style={{textAlign: "left", width: "100%"}}>{a.label}</span>
        ),
    },
    {
        Header: translations.pricesExcelColThree,
        accessor: "oldPrice",
        width: 150,
    },
    {
        id: "newPrice",
        Header: <b>{translations.pricesExcelColFour}</b>,
        width: 130,
        accessor: (a: IDroopyIdAndPrices) => {
            const handleChange = (
                event: React.ChangeEvent<HTMLInputElement>,
                data: InputOnChangeData
            ) => onChangeNewPrice(a.droopyId, a.supplierRef, parseFloat(data.value))
            return (
                <Input
                    style={{fontWeight: "bold", width: "90%"}}
                    className="price-input"
                    transparent
                    type="number"
                    value={a.newPrice || a.oldPrice}
                    onChange={handleChange}
                />
            )
        },
    },
    {
        id: "priceEvolution",
        Header: translations.pricesExcelColFive,
        width: 100,
        accessor: (a: IDroopyIdAndPrices) => {
            const change = ((a.newPrice || a.oldPrice) / a.oldPrice) * 100 - 100
            return change > 0 ? `+${change.toFixed(2)}%` : `${change.toFixed(2)}%`
        },
    },
]

// ------------------------ INITIALIZER ------------------------

const mapStateToProps = (state: DropitState, props: any) => {
    return {
        // formId: permanentFormIds.MULTI_MODIF,
        activeLang: getActiveLang(state),
        state: state.productForm as ProductFormState,
        selectedProducts: props.selectedRefs as IReferenceIds[],
        // vat: state.miscellaneous.vat,
        // customs: state.miscellaneous.customs,
        // territoi: state.miscellaneous.territoi,
        // brands: state.miscellaneous.brands,
        // techPropNodes: [state.miscellaneous.techTree.data],
        // techRawData: state.miscellaneous.techTree.rawData,
        // comPropNodes: [state.miscellaneous.comTree.data],
        // comRawData: state.miscellaneous.comTree.rawData,
        role: getUserRole(state),
        supplier: getUserSelectedSupplier(state),
        translations: getTranslationsByScopes(state, [
            "multiModif",
            "productForm",
            "productList",
            "fieldErrors",
        ]) as ITranslations["fieldErrors"] &
            ITranslations["multiModif"] &
            ITranslations["productForm"] &
            ITranslations["productList"],
    }
}

const mapDispatchToProps = (dispatch: any) => ({
    refreshProductList: () => dispatch(fetchProducts()),
    actions: getActions(dispatch),
})

export default connect<any, any, any, DropitState>(
    mapStateToProps,
    mapDispatchToProps
)(MultiModifPricesModal)
